import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faPaw,
  faLeaf,
  faLandmark,
  faTree,
  faClock,
  faSun,
  faTimes,
  faCheck,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';

const heroImage = 'https://BelvoirCastle.b-cdn.net/Banners/flowerBan.webp';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 768px) {
    height: 360px;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
  text-shadow: 0 1px 4px rgba(0,0,0,0.3);

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem 4rem;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 700px;
  margin: 0 auto 1rem;
`;

const GroupSaverBox = styled.div`
  background: linear-gradient(135deg, rgba(157, 109, 73, 0.12) 0%, rgba(157, 109, 73, 0.18) 100%);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1.25rem 1.5rem;
  border-radius: 10px;
  margin: 1.5rem auto 2rem;
  max-width: 700px;
  text-align: center;

  p {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.7;
    color: ${props => props.theme.colors.primary};
  }

  strong {
    color: ${props => props.theme.colors.accent};
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 2rem;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.a`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  }

  ${props => props.$featured && `
    border-color: ${props.theme.colors.accent};
    background: linear-gradient(180deg, ${props.theme.colors.offWhite} 0%, white 15%);
    box-shadow: 0 12px 45px rgba(0,0,0,0.08);

    &:hover {
      box-shadow: 0 20px 50px rgba(0,0,0,0.12);
    }
  `}
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.5rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 0 16px 0 12px;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const OptionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.85rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-right: ${props => props.$hasBadge ? '120px' : '0'};
  line-height: 1.3;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: ${props => props.theme.colors.accent};
    margin-top: 0.5rem;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    padding-right: 0;
  }
`;

const OptionSubtitle = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  margin: 0 0 1rem;
`;

const OptionDescription = styled.div`
  font-size: 1.05rem;
  line-height: 1.75;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  flex-grow: 1;

  p {
    margin: 0 0 0.75rem;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 1.35rem;
  }

  li {
    margin: 0.4rem 0;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;

  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.5rem 0;
    font-size: 1rem;
  }
`;

const ListIcon = styled(FontAwesomeIcon).attrs({ 'aria-hidden': true })`
  color: ${props => props.theme.colors.accent};
  width: 1rem;
  flex-shrink: 0;
`;

const InlineIcon = styled(FontAwesomeIcon).attrs({ 'aria-hidden': true })`
  color: ${props => props.theme.colors.accent};
  margin-right: 0.35rem;
`;

const CTAButton = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 1.75rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  text-align: center;
  margin-top: auto;
  transition: all 0.25s ease;

  ${OptionCard}:hover & {
    transform: scale(1.02);
    filter: brightness(1.05);
  }
`;

const CTAButtonSecondary = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 1.75rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  text-align: center;
  margin-top: auto;
  transition: all 0.25s ease;

  ${OptionCard}:hover & {
    transform: scale(1.02);
  }
`;

const BertieHighlight = styled.div`
  background: linear-gradient(135deg, rgba(157, 109, 73, 0.12) 0%, rgba(157, 109, 73, 0.08) 100%);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1rem 1.25rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

/* ─── Modal (same structure as Easter) ─── */
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)   scale(1);    }
`;

const decorBounce = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50%       { transform: translateY(-8px) rotate(4deg); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(30, 20, 10, 0.65);
  backdrop-filter: blur(3px);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.35s ease forwards;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 20px;
  max-width: 560px;
  width: 100%;
  position: relative;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0,0,0,0.25);
  animation: ${slideUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #101d40 0%, #1a2f5e 100%);
  padding: 2.25rem 2rem 1.75rem;
  text-align: center;
  position: relative;
`;

const DecorRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 1.75rem;

  span {
    display: inline-block;
    animation: ${decorBounce} 2.2s ease-in-out infinite;
  }
  span:nth-child(2) { animation-delay: 0.3s; }
  span:nth-child(3) { animation-delay: 0.6s; }
`;

const ModalEyebrow = styled.p`
  color: #9d6d49;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin: 0 0 0.4rem;
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 0.5rem;
  text-shadow: 0 2px 6px rgba(0,0,0,0.2);

  @media (max-width: 480px) {
    font-size: 1.65rem;
  }
`;

const ModalTagline = styled.p`
  color: rgba(255,255,255,0.92);
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
`;

const DiscountBanner = styled.div`
  background: linear-gradient(90deg, #9d6d49 0%, #b8894f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
`;

const ModalBody = styled.div`
  padding: 1.75rem 2rem 2rem;

  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem 1.75rem;
  }
`;

const PerksGrid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 1rem;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const PerkItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.97rem;
  color: #101d40;
  line-height: 1.4;
`;

const PerkCheck = styled(FontAwesomeIcon)`
  color: #9d6d49;
  margin-top: 0.15rem;
  flex-shrink: 0;
`;

const GroupSaverHighlight = styled.div`
  background: linear-gradient(135deg, rgba(157, 109, 73, 0.08) 0%, rgba(157, 109, 73, 0.14) 100%);
  border-left: 4px solid #9d6d49;
  border-radius: 8px;
  padding: 0.9rem 1.1rem;
  margin-bottom: 1.5rem;
  font-size: 0.97rem;
  color: #101d40;
  line-height: 1.55;

  strong {
    color: #9d6d49;
  }
`;

const ModalCTA = styled.a`
  display: block;
  background: linear-gradient(135deg, #9d6d49 0%, #b8894f 100%);
  color: #fff;
  text-align: center;
  padding: 1.05rem 1.5rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  text-decoration: none;
  transition: filter 0.2s ease, transform 0.2s ease;
  margin-bottom: 0.75rem;
  box-shadow: 0 6px 20px rgba(157, 109, 73, 0.35);

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-2px);
  }
`;

const ModalSkip = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #888;
  font-size: 0.9rem;
  cursor: pointer;
  text-align: center;
  padding: 0.25rem;
  transition: color 0.2s;

  &:hover {
    color: #555;
    text-decoration: underline;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 1;

  &:hover {
    background: rgba(255,255,255,0.35);
  }
`;

const castleEntryUrl =
  'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=360f48e1-8680-4719-9d2e-fc87a84c974b';
const gardenPlaygroundUrl =
  'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=afa92306-adc0-4470-9faa-15ff1c7aa429';

const MayDayBankHoliday = () => {
  const [searchParams] = useSearchParams();
  const skipModal = searchParams.get('from') === 'popup';

  const [showModal, setShowModal] = useState(false);
  const showMayDayModal = false; // Set to true to show the May Day popup again

  useEffect(() => {
    if (!showMayDayModal || skipModal) return;
    const timer = setTimeout(() => setShowModal(true), 900);
    return () => clearTimeout(timer);
  }, [skipModal, showMayDayModal]);

  const closeModal = () => setShowModal(false);

  const meta = {
    title: 'May Day Bank Holiday at Belvoir Castle | Castle & Gardens or Gardens & Playground',
    description:
      'Visit Belvoir on the Early May bank holiday — Monday 4 May 2026. Choose The Ultimate May Day Experience with Castle & gardens (Animal Park free), or gardens & playground (add Animal Park for £4.50). Group Saver available.',
    canonical: 'https://www.belvoircastle.com/may-day-bank-holiday',
  };

  return (
    <Page>
      {showModal && (
        <Overlay onClick={closeModal}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <CloseBtn onClick={closeModal} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </CloseBtn>
              <DecorRow>
                <FontAwesomeIcon icon={faLeaf} />
                <FontAwesomeIcon icon={faSun} />
                <FontAwesomeIcon icon={faTree} />
              </DecorRow>
              <ModalEyebrow>May Day at Belvoir Castle</ModalEyebrow>
              <ModalTitle>Your Ultimate May Day Starts Here</ModalTitle>
              <ModalTagline>Monday 4 May 2026 — Castle &amp; gardens or gardens &amp; playground</ModalTagline>
            </ModalHeader>

            <DiscountBanner>
              <FontAwesomeIcon icon={faTag} />
              Group Saver discount — the more you add, the more you save
            </DiscountBanner>

            <ModalBody>
              <PerksGrid>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Adventure Playground access</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Full Castle entry on the ultimate package</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Beautiful gardens &amp; parkland</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Gardens &amp; playground option for outdoor days</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Animal Park FREE with Castle &amp; gardens</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Animal Park £4.50 with gardens &amp; playground</span>
                </PerkItem>
              </PerksGrid>

              <GroupSaverHighlight>
                <strong>Bring the whole family and save more.</strong> Our Group Saver discount means every extra ticket
                costs you less — perfect for May Day, Monday 4 May.
              </GroupSaverHighlight>

              <ModalCTA href={castleEntryUrl} target="_blank" rel="noopener noreferrer" onClick={closeModal}>
                Book the Ultimate May Day Experience →
              </ModalCTA>
              <ModalSkip onClick={closeModal}>Just browsing — show me all options</ModalSkip>
            </ModalBody>
          </Modal>
        </Overlay>
      )}

      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta
          name="keywords"
          content="May Day, bank holiday, Belvoir Castle, garden entry, Castle entry, Adventure Playground, Animal Park, Group Saver, Leicestershire"
        />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>May Day at Belvoir</Badge>
          <Title>May Day Bank Holiday</Title>
          <Strapline>
            Monday 4 May 2026 — choose The Ultimate May Day Experience or gardens &amp; playground
          </Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <IntroSection>
          <IntroText>
            Join us on <strong>Monday 4 May 2026</strong> — the Early May bank holiday. The <strong>Adventure Playground</strong>{' '}
            is at the heart of a family day out. Choose <strong>The Ultimate May Day Experience</strong> for Castle, gardens
            and playground — with <strong>Animal Park free</strong> — or <strong>Gardens &amp; Playground</strong> for gardens
            and playground access (add Animal Park for £4.50).
          </IntroText>
        </IntroSection>

        <GroupSaverBox>
          <p>
            <strong>Enjoy the Belvoir Group Saver discount</strong> — the more you add, the more you save. Bring family
            or friends on 4 May and make the most of your day.
          </p>
        </GroupSaverBox>

        <CardsGrid>
          <OptionCard href={castleEntryUrl} target="_blank" rel="noopener noreferrer" $featured>
            <RecommendedBadge>
              <FontAwesomeIcon icon={faStar} /> Best value
            </RecommendedBadge>
            <OptionTitle $hasBadge>The Ultimate May Day Experience – Castle &amp; Gardens</OptionTitle>
            <OptionSubtitle>Everything included for your best day out</OptionSubtitle>
            <OptionDescription>
              <p>
                Make the most of May Day at Belvoir with our full-day experience: the Castle, gardens and parkland, plus
                the Adventure Playground — <strong>Monday 4 May 2026 only</strong>.
              </p>
              <BertieHighlight>
                <InlineIcon icon={faLandmark} />
                Full Castle entry — state rooms and spring views for the whole family.
              </BertieHighlight>
              <FeatureList>
                <li>
                  <ListIcon icon={faLeaf} /> Adventure Playground
                </li>
                <li>
                  <ListIcon icon={faLandmark} /> Castle entry &amp; state rooms
                </li>
                <li>
                  <ListIcon icon={faTree} /> Gardens and parkland
                </li>
                <li>
                  <ListIcon icon={faClock} /> Full day out — best value
                </li>
              </FeatureList>
              <BertieHighlight>Add Animal Park for free — exclusive May Day offer</BertieHighlight>
            </OptionDescription>
            <CTAButton>Book the Ultimate Experience →</CTAButton>
          </OptionCard>

          <OptionCard href={gardenPlaygroundUrl} target="_blank" rel="noopener noreferrer">
            <OptionTitle>Gardens &amp; Playground</OptionTitle>
            <OptionSubtitle>Playground + gardens access</OptionSubtitle>
            <OptionDescription>
              <p>
                Enjoy the gardens and the Adventure Playground on <strong>Monday 4 May 2026</strong> — perfect if you want
                a brilliant outdoor day without Castle entry.
              </p>
              <FeatureList>
                <li>
                  <ListIcon icon={faLeaf} /> Adventure Playground
                </li>
                <li>
                  <ListIcon icon={faTree} /> Gardens access included
                </li>
                <li>
                  <ListIcon icon={faSun} /> Great for a spring day out
                </li>
              </FeatureList>
              <BertieHighlight>Add Animal Park for £4.50</BertieHighlight>
            </OptionDescription>
            <CTAButtonSecondary>Book Gardens &amp; Playground →</CTAButtonSecondary>
          </OptionCard>
        </CardsGrid>
      </Content>

      <Footer />
    </Page>
  );
};

export default MayDayBankHoliday;
